import type { TFunction } from 'i18next'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Link } from 'react-router-dom'

import { buttonClasses } from '@/design-system/Button'
import { IncludedList } from '@/design-system/IncludedList'
import { Badge, Overline, Section, SectionTitle } from '@/design-system/primitives'
import { QuantityStepper } from '@/features/tickets/components'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/money'

import type { PublicDrinkCatalog, PublicTableType } from './api'

export interface TableSelection {
  table_type_id: string | null
  party_size: number
  /** Nombre de tables identiques — un groupe de vingt prend cinq tables de 4. */
  quantity: number
  package_id: string | null
  /** `drink_item_id` → quantité, **par table**. */
  items: Record<string, number>
}

/** « Personnaliser cette table » : la table affichée, et sa formule si elle en a une. */
export function customizeHref(tableTypeId: string, packageId: string | null): string {
  const query = new URLSearchParams({ table: tableTypeId })
  if (packageId) query.set('package', packageId)
  return `/personnaliser?${query.toString()}`
}

export const EMPTY_TABLE: TableSelection = {
  table_type_id: null,
  party_size: 2,
  quantity: 1,
  package_id: null,
  items: {},
}

function Price({ minor, currency, locale }: { minor: number; currency: string; locale: string }) {
  return (
    <span className="shrink-0 tabular-nums text-champagne-300">
      {formatMoney({ amount_minor: minor, currency }, locale)}
    </span>
  )
}

function capacityLabel(tableType: PublicTableType, t: TFunction) {
  return tableType.capacity_min === tableType.capacity_max
    ? t('tables.capacityFixed', { max: tableType.capacity_max })
    : t('tables.capacity', { min: tableType.capacity_min, max: tableType.capacity_max })
}

/** Carte d'un type de table : capacité, prix de base, ce qui est compris. */
export function TableTypeCard({
  tableType,
  selected,
  onSelect,
  locale,
}: {
  tableType: PublicTableType
  selected: boolean
  onSelect: () => void
  locale: string
}) {
  const { t } = useTranslation()
  const soldOut = tableType.availability === 'sold_out'

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={soldOut}
      aria-pressed={selected}
      className={cn(
        'w-full overflow-hidden rounded-lg border text-left transition',
        selected ? 'border-ember-400 shadow-ember' : 'border-line-subtle hover:border-line-strong',
        soldOut && 'cursor-not-allowed opacity-50',
      )}
    >
      {/* Montrer la table met en confiance : on voit ce qu'on réserve. */}
      {tableType.image_url ? (
        <img
          src={tableType.image_url}
          alt=""
          className="block h-36 w-full object-cover"
          loading="lazy"
        />
      ) : null}

      <div className="p-6">
      <div className="flex items-baseline justify-between gap-4">
        <p className="font-display text-heading-md text-content-primary">{tableType.name}</p>
        <Price
          minor={tableType.base_price_minor}
          currency={tableType.currency}
          locale={locale}
        />
      </div>

      <p className="mt-1 text-sm text-content-secondary">{capacityLabel(tableType, t)}</p>
      {tableType.description ? (
        <p className="mt-3 text-sm text-content-secondary">{tableType.description}</p>
      ) : null}

      <IncludedList
        label={t('tables.included')}
        items={tableType.included}
        className="mt-3"
      />

      {tableType.included_bottles_allowance > 0 ? (
        <p className="mt-1 text-sm text-content-muted">
          {t('tables.includedFree', { count: tableType.included_bottles_allowance })}
        </p>
      ) : null}

      {tableType.min_spend_minor ? (
        <p className="mt-1 text-sm text-content-muted">
          {t('tables.minSpend', {
            amount: formatMoney(
              { amount_minor: tableType.min_spend_minor, currency: tableType.currency },
              locale,
            ),
          })}
        </p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {soldOut ? <Badge tone="neutral">{t('tables.soldOut')}</Badge> : null}
        {!soldOut && tableType.remaining !== null ? (
          <Badge tone={tableType.availability === 'low' ? 'ember' : 'neutral'}>
            {t('tables.remaining', { count: tableType.remaining })}
          </Badge>
        ) : null}
      </div>
      </div>
    </button>
  )
}


/**
 * BUILD YOUR DRINK TABLE.
 *
 * Deux choses seulement : la table retenue, et le nombre de convives.
 * Les tables sont **fixes** — leur composition est celle que l'équipe a
 * définie, pas un panier que le client remplit. Le catalogue des bouteilles
 * se commande le soir venu, depuis la salle.
 * Le prix affiché à droite est **celui du serveur** — recalculé à chaque
 * changement. Aucun total n'est jamais additionné ici.
 */
export function TableComposer({
  catalog,
  selection,
  onChange,
  locale,
  allowCustomization = false,
}: {
  catalog: PublicDrinkCatalog
  selection: TableSelection
  onChange: (selection: TableSelection) => void
  locale: string
  /** « Personnaliser cette table » : ouvert par l'équipe (`tables.allow_customization`). */
  allowCustomization?: boolean
}) {
  const { t } = useTranslation()

  const tableType = catalog.table_types.find((entry) => entry.id === selection.table_type_id)
  // Le stock réel plafonne le sélecteur : proposer huit tables quand il en
  // reste trois ferait échouer le devis après coup, sans expliquer pourquoi.
  const maxTables = Math.max(1, Math.min(tableType?.remaining ?? 20, 20))

  function select(next: PublicTableType) {
    onChange({
      table_type_id: next.id,
      // Le nombre de personnes est ramené dans la capacité du nouveau type.
      party_size: Math.min(Math.max(selection.party_size, next.capacity_min), next.capacity_max),
      quantity: selection.quantity,
      package_id: null,
      items: selection.items,
    })
  }


  /**
   * Le nombre de convives suit toujours la table choisie.
   *
   * Une table pré-sélectionnée depuis la carte (`/billets?table=…`) arrive
   * sans passer par `select()` : le nombre de personnes restait à sa valeur
   * par défaut, et le devis répondait « le nombre de personnes ne correspond
   * pas à la capacité de cette table » avant qu'on ait touché à quoi que ce
   * soit. On le ramène donc dans la capacité dès que le type est connu.
   */
  useEffect(() => {
    if (!tableType) return
    const clamped = Math.min(
      Math.max(selection.party_size, tableType.capacity_min),
      tableType.capacity_max,
    )
    if (clamped !== selection.party_size) onChange({ ...selection, party_size: clamped })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableType?.id, tableType?.capacity_min, tableType?.capacity_max])

  return (
    <div className="space-y-8">
      {/* On arrive ici en ayant cliqué « Réserver cette table ». Réafficher
          les six autres formules, c'est redemander un choix déjà fait — et
          risquer de le défaire d'un doigt qui glisse. La table retenue
          s'annonce, et on passe à la suite. */}
      {tableType ? (
        <div>
          <p className="overline text-content-muted">{t('tables.stepType')}</p>
          <p className="mt-3 font-display text-heading-md text-content-primary">
            {tableType.name}
          </p>
          {tableType.included.length > 0 ? (
            <p className="mt-1 text-sm text-content-secondary">
              {tableType.included.join(' · ')}
            </p>
          ) : null}
          {/* Une autre composition ? On part de cette table-ci (et de la
              formule choisie) : l'équipe fixe ensuite le prix. */}
          {allowCustomization ? (
            <Link
              to={customizeHref(tableType.id, selection.package_id)}
              className="mt-2 inline-flex min-h-11 items-center text-sm text-champagne-300 underline"
            >
              {t('tables.customize')}
            </Link>
          ) : null}
        </div>
      ) : (
        <div>
          <p className="overline text-content-muted">{t('tables.stepType')}</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {catalog.table_types.map((entry) => (
              <TableTypeCard
                key={entry.id}
                tableType={entry}
                selected={entry.id === selection.table_type_id}
                onSelect={() => select(entry)}
                locale={locale}
              />
            ))}
          </div>
          {catalog.table_types.length === 0 ? (
            <p className="mt-4 text-content-muted">{t('tables.empty')}</p>
          ) : null}
        </div>
      )}

      {tableType ? (
        <>
          <div>
            <p className="overline text-content-muted">{t('tables.stepParty')}</p>
            {/* Une table de six est une table de six : un curseur qu'on ne
                peut pas bouger est un faux contrôle. On affiche le chiffre. */}
            {tableType.capacity_min === tableType.capacity_max ? (
              <p className="mt-4 flex items-baseline gap-3">
                <span className="font-display text-display-sm tabular-nums text-champagne-300">
                  {tableType.capacity_max}
                </span>
                <span className="text-sm text-content-secondary">
                  {t('tables.seatsFixed', { count: tableType.capacity_max })}
                </span>
              </p>
            ) : (
              <div className="mt-4 flex items-center gap-4">
                <QuantityStepper
                  value={selection.party_size}
                  onChange={(value) =>
                    onChange({
                      ...selection,
                      party_size: Math.min(
                        Math.max(value, tableType.capacity_min),
                        tableType.capacity_max,
                      ),
                    })
                  }
                  max={tableType.capacity_max}
                  label={t('tables.stepParty')}
                />
                <span className="text-sm text-content-secondary">
                  {capacityLabel(tableType, t)}
                </span>
              </div>
            )}
          </div>

          <div>
            <p className="overline text-content-muted">{t('tables.stepCount')}</p>
            <div className="mt-4 flex items-center gap-4">
              <QuantityStepper
                value={selection.quantity}
                onChange={(value) =>
                  onChange({ ...selection, quantity: Math.max(1, Math.min(value, maxTables)) })
                }
                max={maxTables}
                label={t('tables.stepCount')}
              />
              <span className="text-sm text-content-secondary">
                {t('tables.countHint', { count: selection.quantity })}
              </span>
            </div>
          </div>

          {tableType.packages.length > 0 ? (
            <div>
              <p className="overline text-content-muted">{t('tables.package')}</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => onChange({ ...selection, package_id: null })}
                  aria-pressed={selection.package_id === null}
                  className={cn(
                    'rounded-lg border p-4 text-left transition',
                    selection.package_id === null
                      ? 'border-ember-400'
                      : 'border-line-subtle hover:border-line-strong',
                  )}
                >
                  {t('tables.noPackage')}
                </button>

                {tableType.packages.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => onChange({ ...selection, package_id: entry.id })}
                    aria-pressed={selection.package_id === entry.id}
                    className={cn(
                      'rounded-lg border p-4 text-left transition',
                      selection.package_id === entry.id
                        ? 'border-ember-400'
                        : 'border-line-subtle hover:border-line-strong',
                    )}
                  >
                    <div className="flex items-baseline justify-between gap-4">
                      <span className="text-content-primary">{entry.name}</span>
                      <Price
                        minor={entry.package_price_minor}
                        currency={entry.currency}
                        locale={locale}
                      />
                    </div>
                    {entry.contents.length > 0 ? (
                      <p className="mt-2 text-sm text-content-muted">
                        {entry.contents.join(' · ')}
                      </p>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {/* Les tables sont **fixes** : leur composition est celle que
              l'équipe a définie, affichée plus haut. Le catalogue complet des
              bouteilles se commande le soir venu depuis `/commander`, avec la
              carte — pas des semaines à l'avance dans un formulaire de
              réservation. Seules les bouteilles marquées « sur commande » se
              réservent en avance, et elles ont leur propre carte. */}
        </>
      ) : (
        <p className="text-content-muted">{t('tables.chooseTable')}</p>
      )}

      {catalog.admission_notice ? (
        <p className="rounded-md border border-line-subtle bg-bg-overlay p-4 text-sm text-content-secondary">
          ⚠️ {catalog.admission_notice}
        </p>
      ) : null}
    </div>
  )
}

/** Les tables, sur la page d'accueil. Lecture seule. */
export function TablesSection({
  catalog,
  locale,
  showCatalog = true,
  allowCustomization = false,
  intro,
}: {
  catalog: PublicDrinkCatalog
  locale: string
  /** Les formules à prix affiché. Fermées, elles restent en base. */
  showCatalog?: boolean
  /**
   * « Personnaliser cette table » sous chaque table : le client modifie la
   * composition, l'équipe fixe le prix. Remplace l'ancien formulaire libre
   * « table sur mesure », retiré du site.
   */
  allowCustomization?: boolean
  /** Texte libre et photo, écrits en back-office. */
  intro?: { title: string; body: string; image_url: string | null } | undefined
}) {
  const { t } = useTranslation()

  const types = showCatalog ? catalog.table_types : []
  // Rien à montrer, rien à lire : la section disparaît plutôt que d'afficher
  // un titre suivi de vide.
  if (types.length === 0 && !intro) return null

  return (
    <Section id="tables">
      <Overline>Drink tables</Overline>
      <SectionTitle>{intro?.title || t('tables.title')}</SectionTitle>

      {/* Ce que l'équipe veut dire des tables, avec sa photo. Écrit en
          back-office : décrire une table ne demande pas un développeur. */}
      {intro ? (
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:items-start">
          {intro.image_url ? (
            <img
              src={intro.image_url}
              alt=""
              loading="lazy"
              className="w-full rounded-lg object-cover"
            />
          ) : null}
          {intro.body ? (
            <p className="whitespace-pre-line leading-relaxed text-content-secondary">
              {intro.body}
            </p>
          ) : null}
        </div>
      ) : null}

      <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {types.map((tableType) => {
          const soldOut = tableType.availability === 'sold_out'

          return (
            <li key={tableType.id}>
              <article className="lift flex h-full flex-col overflow-hidden rounded-lg border border-line-subtle bg-bg-elevated">
                {/* Montrer la table met en confiance : on voit ce qu'on réserve. */}
                {tableType.image_url ? (
                  <img
                    src={tableType.image_url}
                    alt=""
                    className="block h-40 w-full object-cover"
                    loading="lazy"
                  />
                ) : null}

                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-baseline justify-between gap-4">
                    <p className="font-display text-heading-md text-content-primary">
                      {tableType.name}
                    </p>
                    <Price
                      minor={tableType.base_price_minor}
                      currency={tableType.currency}
                      locale={locale}
                    />
                  </div>

                  <p className="mt-1 text-sm text-content-secondary">
                    {capacityLabel(tableType, t)}
                  </p>

                  {/* La promesse qui décide : la table paie l'entrée du groupe. */}
                  {tableType.includes_admission ? (
                    <p className="mt-3 text-sm text-champagne-300">
                      {t('tables.admissionIncluded', { count: tableType.capacity_max })}
                    </p>
                  ) : null}

                  <IncludedList
                    label={t('tables.included')}
                    items={tableType.included}
                    className="mt-3"
                  />

                  {/* Sans ce bouton, la fiche était un cul-de-sac : le
                      configurateur vivait dans le tunnel de billetterie, à une
                      étape que rien ne signalait. */}
                  {soldOut ? (
                    <p className="mt-5 text-sm text-content-muted">{t('tables.soldOut')}</p>
                  ) : (
                    <div className="mt-5 flex flex-wrap items-center gap-2">
                      <Link
                        to={`/billets?table=${tableType.id}`}
                        className={buttonClasses({ size: 'sm' })}
                      >
                        {t('tables.book')}
                      </Link>
                      {/* La même table, autrement composée : une bouteille en
                          moins, une autre à la place. L'équipe fixe le prix. */}
                      {allowCustomization ? (
                        <Link
                          to={customizeHref(tableType.id, null)}
                          className={buttonClasses({ size: 'sm', variant: 'outline' })}
                        >
                          {t('tables.customize')}
                        </Link>
                      ) : null}
                    </div>
                  )}
                </div>
              </article>
            </li>
          )
        })}
      </ul>

      {catalog.admission_notice ? (
        <div className="glass mt-6 max-w-2xl rounded-lg px-6 py-5">
          <p className="text-sm text-content-secondary">⚠️ {catalog.admission_notice}</p>
        </div>
      ) : null}
    </Section>
  )
}
